import { useMemo, useState } from 'react';
import clsx from 'clsx';
import Sheet from './Sheet.jsx';
import Icon from './Icon.jsx';
import { haptic } from '../app/settingsStore.js';
import styles from './PickerSheet.module.scss';

const SEARCH_THRESHOLD = 8;

/**
 * A full-height list of large, tappable options in a sheet (our replacement for <select>).
 * options: [{ value, label, description?, icon?, depth?, disabled? }]
 */
export default function PickerSheet({ open, onClose, title, subtitle, options, value, onChange, footer }) {
  return open ? (
    <Inner
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      options={options}
      value={value}
      onChange={onChange}
      footer={footer}
    />
  ) : null;
}

function Inner({ onClose, title, subtitle, options, value, onChange, footer }) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => `${o.label} ${o.description ?? ''}`.toLowerCase().includes(s));
  }, [options, q]);

  return (
    <Sheet open onClose={onClose} title={title} subtitle={subtitle}>
      {options.length > SEARCH_THRESHOLD ? (
        <div className={styles.search}>
          <Icon name="browse" size={18} />
          <input
            type="search"
            placeholder="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={`Search ${title}`}
          />
        </div>
      ) : null}
      <div className={styles.list} role="listbox" aria-label={title}>
        {shown.map((o) => {
          const on = o.value === value;
          return (
            <button
              key={String(o.value)}
              type="button"
              role="option"
              aria-selected={on}
              disabled={o.disabled}
              className={clsx(styles.option, on && styles.on)}
              style={{ '--depth': o.depth ?? 0 }}
              onClick={() => {
                haptic(5);
                onChange(o.value);
                onClose();
              }}
            >
              {o.icon ? (
                <span className={styles.icon}>
                  <Icon name={o.icon} size={20} />
                </span>
              ) : null}
              <span className={styles.text}>
                <span className={styles.label}>{o.label}</span>
                {o.description ? <span className={styles.desc}>{o.description}</span> : null}
              </span>
              <span className={styles.radio} aria-hidden="true">
                {on ? <Icon name="check" size={16} stroke={3} /> : null}
              </span>
            </button>
          );
        })}
        {!shown.length ? <p className={styles.none}>Nothing matches “{q}”.</p> : null}
      </div>
      {footer ? <div className={styles.extra}>{footer}</div> : null}
    </Sheet>
  );
}
